"use client"

import { useState, useEffect } from "react"
import { useMsal } from "@azure/msal-react"
import type { AuthenticationResult } from "@azure/msal-browser"
import { loginRequest, alternativeLoginRequest } from "../config/authConfig"

export interface AuthState {
  isAuthenticated: boolean
  accessToken: string | null
  isLoading: boolean
  error: string | null
  user: any
}

export const useAuth = () => {
  const { instance, accounts } = useMsal()
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    accessToken: null,
    isLoading: true,
    error: null,
    user: null,
  })

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (accounts.length > 0) {
          // Try to get access token silently
          let response: AuthenticationResult | null = null

          try {
            response = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            })
          } catch (silentError) {
            console.log("Silent token acquisition failed, trying alternative scopes...")
            try {
              // Try with alternative scopes
              response = await instance.acquireTokenSilent({
                ...alternativeLoginRequest,
                account: accounts[0],
              })
            } catch (alternativeError) {
              console.error("Both silent token acquisition methods failed:", alternativeError)
              throw alternativeError
            }
          }

          if (response?.accessToken) {
            setAuthState({
              isAuthenticated: true,
              accessToken: response.accessToken,
              isLoading: false,
              error: null,
              user: accounts[0],
            })
          }
        } else {
          setAuthState((prev) => ({ ...prev, isLoading: false }))
        }
      } catch (error: any) {
        console.error("Auth initialization error:", error)
        setAuthState({
          isAuthenticated: false,
          accessToken: null,
          isLoading: false,
          error: "Authentication failed",
          user: null,
        })
      }
    }

    initializeAuth()
  }, [instance, accounts])

  const login = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }))

      let response: AuthenticationResult | null = null

      try {
        response = await instance.loginPopup(loginRequest)
      } catch (loginError: any) {
        console.log("Login with specific scopes failed, trying alternative...")
        try {
          // Try with alternative scopes
          response = await instance.loginPopup(alternativeLoginRequest)
        } catch (alternativeError) {
          console.error("Both login methods failed:", alternativeError)
          throw alternativeError
        }
      }

      if (response?.accessToken && response?.account) {
        setAuthState({
          isAuthenticated: true,
          accessToken: response.accessToken,
          isLoading: false,
          error: null,
          user: response.account,
        })
      } else {
        throw new Error("Login response is invalid or missing required data")
      }
    } catch (error: any) {
      console.error("Login error:", error)
      setAuthState({
        isAuthenticated: false,
        accessToken: null,
        isLoading: false,
        error: error.message || "Login failed",
        user: null,
      })
    }
  }

  const logout = () => {
    instance.logoutPopup()
    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      isLoading: false,
      error: null,
      user: null,
    })
  }

  const refreshToken = async (): Promise<string | undefined> => {
    try {
      if (accounts.length > 0) {
        let response: AuthenticationResult | null = null

        try {
          response = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          })
        } catch (silentError) {
          try {
            // Try with alternative scopes
            response = await instance.acquireTokenSilent({
              ...alternativeLoginRequest,
              account: accounts[0],
            })
          } catch (alternativeError) {
            console.error("Token refresh failed with both methods:", alternativeError)
            throw alternativeError
          }
        }

        if (response && response.accessToken !== undefined) {
          if (response) {
            setAuthState((prev) => ({
              ...prev,
              accessToken: response ? response.accessToken : '',
            }))
          }

          return response.accessToken
        } else {
          throw new Error("Token refresh response is invalid or missing access token")
        }
      } else {
        throw new Error("No accounts available for token refresh")
      }
    } catch (error) {
      console.error("Token refresh error:", error)
      throw error
    }
  }

  return {
    ...authState,
    login,
    logout,
    refreshToken,
  }
}
